import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { ButtonBase } from '@mui/material';
import { IconButton } from 'components';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ChapterAccordionContext from 'components/ChapterAccordion/ChapterAccordionContext';

const useStyles = makeStyles((theme) => {
  const commonOutline = `2px solid ${theme.palette.shelfBlue[800]}`;
  const commonOutlineOffset = '-2px';
  return {
    root: {
      background: 'transparent',
      ...theme.typography.body1,
      alignItems: 'center',
      borderBottom: ({ expanded }) =>
        expanded ? `1px solid ${theme.palette.grey[300]}` : 'unset',
      display: 'flex',
      minHeight: theme.spacing(7),
      paddingLeft: ({ orientation }) =>
        orientation === 'left' && theme.spacing(1),
      paddingRight: ({ orientation }) =>
        orientation === 'right' && theme.spacing(1),
    },
    accordionContent: {
      width: '100%',
    },
    buttonBase: {
      '&:hover': {
        backgroundColor: theme.palette.grey[200], // #F2F2F2
        outline: commonOutline,
        outlineOffset: commonOutlineOffset,
      },
      '&:focus': {
        backgroundColor: '#F5F5F5',
        outline: commonOutline,
        outlineOffset: commonOutlineOffset,
      },
      '&:focus-visible': {
        outline: commonOutline,
        outlineOffset: commonOutlineOffset,
      },
      '&.MuiButtonBase-root': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        border: 'unset',
        padding: theme.spacing(0.5, 0.5, 0.5, 2),
        borderBottom: ({ expanded }) =>
          expanded ? `1px solid ${theme.palette.grey[300]}` : 'unset',
      },
    },
    buttonBaseAccordionContent: {
      color: theme.palette.greyBlue['900'],
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(14), // 0.875rem
      fontWeight: 500,
      lineHeight: '1.3125rem',
      textAlign: 'left',
    },
    expanded: {},
    focused: {},
    iconButton: {
      color: theme.palette.greyBlue[700],
      height: theme.spacing(5.5),
      width: theme.spacing(5.5),

      '&.MuiIconButton-root': {
        '&:hover': {
          backgroundColor: theme.palette.grey[200], // #F2F2F2
        },
      },
    },
    iconContainer: {
      alignItems: 'center',
      color: theme.palette.greyBlue[700],
      display: 'flex',
      justifyContent: 'center',
      minHeight: theme.spacing(5.5),
      minWidth: theme.spacing(5.5),
    },
    hidden: {
      display: 'none',
    },
  };
});

const AccordionExpandButton = ({
  ariaControls,
  ariaLabelChapterTitle,
  expanded,
  id,
  onClick,
}) => {
  const classes = useStyles();

  return (
    <>
      <span
        className={clsx(classes.hidden, 'noTranslate')}
        id={`expand-button-label-${id}`}
      >
        {ariaLabelChapterTitle}
      </span>
      <IconButton
        aria-controls={ariaControls}
        aria-expanded={expanded}
        aria-labelledby={`expand-button-label-${id}`}
        className={classes.iconButton}
        onClick={onClick}
      >
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    </>
  );
};

const AccordionExpandIcon = ({ expanded }) => {
  const classes = useStyles();

  return (
    <div className={classes.iconContainer}>
      {expanded ? <ExpandLess /> : <ExpandMore />}
    </div>
  );
};

const ButtonBaseAccordion = ({ children, expanded, toggle, ...props }) => {
  const classes = useStyles({ expanded });

  return (
    <h3>
      <ButtonBase
        aria-expanded={expanded}
        className={clsx(classes.buttonBase)}
        disableRipple
        focusRipple={false}
        onClick={toggle}
        role="button"
        {...props}
      >
        {children}
      </ButtonBase>
    </h3>
  );
};

const ChapterAccordionSummary = ({
  'aria-controls': ariaControls,
  ariaLabelChapterTitle,
  children,
  className,
  id,
  isNavItem = false,
  ...props
}) => {
  const { expanded, toggle, orientation } = React.useContext(
    ChapterAccordionContext,
  );

  const classes = useStyles({ expanded, orientation });

  const orientationLeft = Boolean(orientation === 'left');
  const orientationRight = Boolean(
    orientation === 'right' || orientation === undefined,
  ); // default orientation

  const buttonProps = {
    ariaControls,
    ariaLabelChapterTitle,
    expanded,
    id,
    onClick: toggle,
  };

  return isNavItem ? (
    <div className={clsx(classes.root, className)} {...props}>
      {orientationLeft && <AccordionExpandButton {...buttonProps} />}
      <div className={classes.accordionContent}>{children}</div>
      {orientationRight && <AccordionExpandButton {...buttonProps} />}
    </div>
  ) : (
    <ButtonBaseAccordion expanded={expanded} toggle={toggle} {...props}>
      {orientationLeft && <AccordionExpandIcon expanded={expanded} />}
      <div
        className={clsx(
          classes.accordionContent,
          classes.buttonBaseAccordionContent,
        )}
      >
        {children}
      </div>
      {orientationRight && <AccordionExpandIcon expanded={expanded} />}
    </ButtonBaseAccordion>
  );
};

ChapterAccordionSummary.propTypes = {
  /**
   * Necessary to properly associate aria-controls to the AccordionExpandButton.
   * Corresponding ID is set in ChapterAccordion on Collapse child <div>.
   */
  'aria-controls': PropTypes.string.isRequired,
  /**
   * Chapter title used to generate a unique aria-label for the expand button or button base.
   */
  ariaLabelChapterTitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isNavItem: PropTypes.bool,
};

export default ChapterAccordionSummary;
